'use client';

import { AlertTriangle, Loader2 } from 'lucide-react';

import { BaseModal } from '@/components/ui/BaseModal';
import { Button } from '@/components/ui/button';
import { CIToken } from '@/lib/query/useCITokens';

interface RevokeCITokenModalProps {
  isOpen: boolean;
  onClose: () => void;
  token: CIToken | null;
  onConfirm: () => void;
  isPending?: boolean;
}

export function RevokeCITokenModal({
  isOpen,
  onClose,
  token,
  onConfirm,
  isPending = false,
}: RevokeCITokenModalProps) {
  if (!token) return null;

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Revoke CI token"
      size="sm"
    >
      <div className="space-y-4">
        <div className="bg-destructive/10 flex items-start gap-3 rounded-md p-4">
          <AlertTriangle className="text-destructive mt-0.5 h-5 w-5 flex-shrink-0" />
          <div className="space-y-1">
            <p className="font-medium">
              Are you sure you want to revoke this token?
            </p>
            <p className="text-muted-foreground text-sm">
              <strong>{token.name}</strong> will stop working immediately. Any
              CI job or headless run using it will fail until you create a new
              token. This cannot be undone.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Revoking...
              </>
            ) : (
              'Revoke token'
            )}
          </Button>
        </div>
      </div>
    </BaseModal>
  );
}
